/**
 * Client-side encryption service for end-to-end encryption.
 * Uses Web Crypto API for all cryptographic operations.
 *
 * Architecture:
 * - User Password → PBKDF2 → Master Key (256-bit)
 * - Master Key encrypts user's RSA private key
 * - Each file encrypted with unique AES-256-GCM key
 * - File keys encrypted with RSA public keys for sharing
 */

// Encryption constants
const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 32; // bytes
const AES_KEY_LENGTH = 256; // bits
const RSA_KEY_LENGTH = 2048; // bits
const IV_LENGTH = 12; // bytes for GCM
const AUTH_TAG_LENGTH = 128; // bits for GCM

export interface UserEncryptionKeys {
  publicKey: string; // PEM format
  privateKey: string; // PEM format
  encryptedPrivateKey: string; // Base64 encrypted private key
  salt: string; // Base64 salt for PBKDF2
}

export interface EncryptedFile {
  encryptedData: ArrayBuffer;
  fileKey: string; // Base64 encoded AES key
  iv: string; // Base64 encoded initialization vector
}

export interface EncryptedMetadata {
  originalName: string;
  mime: string;
  size: number;
  extension: string;
}

export class CryptoService {
  /**
   * Generate master key from password using PBKDF2.
   */
  static async deriveMasterKey(
    password: string,
    salt: Uint8Array,
    iterations: number = PBKDF2_ITERATIONS
  ): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const passwordBuffer = enc.encode(password);

    // Import password as raw key
    const baseKey = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive AES key using PBKDF2
    const masterKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: iterations,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-GCM', length: AES_KEY_LENGTH },
      true,
      ['encrypt', 'decrypt']
    );

    return masterKey;
  }

  /**
   * Generate RSA key pair for user.
   */
  static async generateUserKeyPair(): Promise<CryptoKeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: RSA_KEY_LENGTH,
        publicExponent: new Uint8Array([1, 0, 1]), // 65537
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt']
    );

    return keyPair;
  }

  /**
   * Export RSA key to PEM format.
   */
  static async exportKeyToPem(
    key: CryptoKey,
    type: 'public' | 'private'
  ): Promise<string> {
    const format = type === 'public' ? 'spki' : 'pkcs8';
    const exported = await crypto.subtle.exportKey(format, key);
    const exportedAsString = String.fromCharCode(...new Uint8Array(exported));
    const exportedAsBase64 = btoa(exportedAsString);
    const pemHeader =
      type === 'public' ? 'PUBLIC KEY' : 'PRIVATE KEY';

    return `-----BEGIN ${pemHeader}-----\n${exportedAsBase64}\n-----END ${pemHeader}-----`;
  }

  /**
   * Import PEM key to CryptoKey.
   */
  static async importPemKey(
    pem: string,
    type: 'public' | 'private'
  ): Promise<CryptoKey> {
    const pemHeader =
      type === 'public' ? 'PUBLIC KEY' : 'PRIVATE KEY';
    const pemContents = pem
      .replace(`-----BEGIN ${pemHeader}-----`, '')
      .replace(`-----END ${pemHeader}-----`, '')
      .replace(/\s/g, '');
    const binaryDer = atob(pemContents);
    const binaryDerArray = new Uint8Array(binaryDer.length);
    for (let i = 0; i < binaryDer.length; i++) {
      binaryDerArray[i] = binaryDer.charCodeAt(i);
    }

    const format = type === 'public' ? 'spki' : 'pkcs8';
    const keyUsages: KeyUsage[] =
      type === 'public' ? ['encrypt'] : ['decrypt'];

    return await crypto.subtle.importKey(
      format,
      binaryDerArray,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      keyUsages
    );
  }

  /**
   * Encrypt RSA private key with master key.
   */
  static async encryptPrivateKey(
    privateKeyPem: string,
    masterKey: CryptoKey
  ): Promise<{ encryptedKey: string; iv: string }> {
    const enc = new TextEncoder();
    const privateKeyBuffer = enc.encode(privateKeyPem);

    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: AUTH_TAG_LENGTH,
      },
      masterKey,
      privateKeyBuffer
    );

    return {
      encryptedKey: this.arrayBufferToBase64(encrypted),
      iv: this.arrayBufferToBase64(iv),
    };
  }

  /**
   * Decrypt RSA private key with master key.
   */
  static async decryptPrivateKey(
    encryptedKey: string,
    iv: string,
    masterKey: CryptoKey
  ): Promise<string> {
    const encryptedBuffer = this.base64ToArrayBuffer(encryptedKey);
    const ivBuffer = this.base64ToArrayBuffer(iv);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
        tagLength: AUTH_TAG_LENGTH,
      },
      masterKey,
      encryptedBuffer
    );

    const dec = new TextDecoder();
    return dec.decode(decrypted);
  }

  /**
   * Generate random AES key for file encryption.
   */
  static async generateFileKey(): Promise<CryptoKey> {
    return await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: AES_KEY_LENGTH,
      },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt file data with AES-GCM.
   */
  static async encryptFile(
    fileData: ArrayBuffer,
    fileKey: CryptoKey
  ): Promise<{ encryptedData: ArrayBuffer; iv: string }> {
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: AUTH_TAG_LENGTH,
      },
      fileKey,
      fileData
    );

    return {
      encryptedData: encrypted,
      iv: this.arrayBufferToBase64(iv),
    };
  }

  /**
   * Decrypt file data with AES-GCM.
   */
  static async decryptFile(
    encryptedData: ArrayBuffer,
    fileKey: CryptoKey,
    iv: string
  ): Promise<ArrayBuffer> {
    const ivBuffer = this.base64ToArrayBuffer(iv);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
        tagLength: AUTH_TAG_LENGTH,
      },
      fileKey,
      encryptedData
    );

    return decrypted;
  }

  /**
   * Encrypt file key with RSA public key (for sharing).
   */
  static async encryptFileKey(
    fileKey: CryptoKey,
    publicKey: CryptoKey
  ): Promise<string> {
    // Export file key as raw bytes
    const rawKey = await crypto.subtle.exportKey('raw', fileKey);

    // Encrypt with RSA public key
    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      rawKey
    );

    return this.arrayBufferToBase64(encrypted);
  }

  /**
   * Decrypt file key with RSA private key.
   */
  static async decryptFileKey(
    encryptedFileKey: string,
    privateKey: CryptoKey
  ): Promise<CryptoKey> {
    const encryptedBuffer = this.base64ToArrayBuffer(encryptedFileKey);

    // Decrypt with RSA private key
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP',
      },
      privateKey,
      encryptedBuffer
    );

    // Import as AES key
    return await crypto.subtle.importKey(
      'raw',
      decrypted,
      {
        name: 'AES-GCM',
        length: AES_KEY_LENGTH,
      },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt metadata (filename, size, mime type).
   */
  static async encryptMetadata(
    metadata: EncryptedMetadata,
    masterKey: CryptoKey
  ): Promise<{ encryptedMetadata: string; iv: string }> {
    const enc = new TextEncoder();
    const metadataJson = JSON.stringify(metadata);
    const metadataBuffer = enc.encode(metadataJson);

    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: AUTH_TAG_LENGTH,
      },
      masterKey,
      metadataBuffer
    );

    return {
      encryptedMetadata: this.arrayBufferToBase64(encrypted),
      iv: this.arrayBufferToBase64(iv),
    };
  }

  /**
   * Decrypt metadata.
   */
  static async decryptMetadata(
    encryptedMetadata: string,
    iv: string,
    masterKey: CryptoKey
  ): Promise<EncryptedMetadata> {
    const encryptedBuffer = this.base64ToArrayBuffer(encryptedMetadata);
    const ivBuffer = this.base64ToArrayBuffer(iv);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
        tagLength: AUTH_TAG_LENGTH,
      },
      masterKey,
      encryptedBuffer
    );

    const dec = new TextDecoder();
    const metadataJson = dec.decode(decrypted);
    return JSON.parse(metadataJson);
  }

  /**
   * Generate random salt for PBKDF2.
   */
  static generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  }

  /**
   * Helper: Convert ArrayBuffer to Base64.
   */
  static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Helper: Convert Base64 to ArrayBuffer.
   */
  static base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Helper: Convert Uint8Array to Base64.
   */
  static uint8ArrayToBase64(array: Uint8Array): string {
    return this.arrayBufferToBase64(array.buffer);
  }

  /**
   * Helper: Convert Base64 to Uint8Array.
   */
  static base64ToUint8Array(base64: string): Uint8Array {
    return new Uint8Array(this.base64ToArrayBuffer(base64));
  }

  /**
   * Stream encryption for large files (chunked).
   */
  static async encryptFileChunk(
    chunk: Uint8Array,
    fileKey: CryptoKey,
    chunkIndex: number
  ): Promise<{ encryptedChunk: ArrayBuffer; iv: string }> {
    // Use chunk index to create deterministic IV (for resumable uploads)
    const iv = new Uint8Array(IV_LENGTH);
    const indexBytes = new Uint8Array(
      new BigUint64Array([BigInt(chunkIndex)]).buffer
    );
    iv.set(indexBytes.slice(0, Math.min(indexBytes.length, IV_LENGTH)));

    // Fill rest with random bytes
    const randomPart = crypto.getRandomValues(
      new Uint8Array(IV_LENGTH - indexBytes.length)
    );
    iv.set(randomPart, indexBytes.length);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: AUTH_TAG_LENGTH,
      },
      fileKey,
      chunk
    );

    return {
      encryptedChunk: encrypted,
      iv: this.arrayBufferToBase64(iv),
    };
  }

  /**
   * Decrypt file chunk.
   */
  static async decryptFileChunk(
    encryptedChunk: ArrayBuffer,
    fileKey: CryptoKey,
    iv: string
  ): Promise<ArrayBuffer> {
    const ivBuffer = this.base64ToArrayBuffer(iv);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
        tagLength: AUTH_TAG_LENGTH,
      },
      fileKey,
      encryptedChunk
    );

    return decrypted;
  }
}
